import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { DefinitionTooltip } from "src/components/common";
import type { WebsiteUsersBreakdown } from "../../data/engagementOverviewTypes";
import { formatTooltipCount, formatTooltipPercent } from "../../utils/engagementOverviewFormat";
import EngagementEmptyState from "./EngagementEmptyState";
import { getEngagementTooltipInfo } from "./getEngagementTooltipInfo";

export interface TotalUsersOnWebsiteProps {
  data: WebsiteUsersBreakdown | null;
  sectionTitle?: string;
  tooltipKey?: string;
}

const TotalUsersOnWebsite: FC<TotalUsersOnWebsiteProps> = ({
  data,
  sectionTitle,
  tooltipKey = "totalUsersOnWebsite",
}) => {
  const { t, i18n } = useTranslation();
  const resolvedSectionTitle = sectionTitle ?? t("engagementOverview.shared.totalUsersSectionTitle");
  const total = data?.totalUsers ?? 0;
  const slices = useMemo(() => data?.slices ?? [], [data]);

  const legendItems = useMemo(() => {
    return slices.map(item => ({
      ...item,
      percent:
        item.legendPercent ??
        (total > 0 ? Math.round((item.value / total) * 100) : 0),
    }));
  }, [slices, total]);

  return (
    <article className="engagement-overview-panel engagement-overview-panel--donut">
      <div className="engagement-overview-panel__header">
        <div className="engagement-overview-panel__heading total-users-donut_heading">
          <h3 className="engagement-overview-panel__heading-title">{resolvedSectionTitle}</h3>
          <DefinitionTooltip
            label={resolvedSectionTitle}
            info={getEngagementTooltipInfo(t, tooltipKey)}
          />
        </div>
      </div>

      {!data || !slices.length || total <= 0 ? (
        <EngagementEmptyState message={t("engagementOverview.shared.totalUsersEmptyMessage")} />
      ) : (
        <>
          <div className="engagement-overview-donut">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Tooltip
                  allowEscapeViewBox={{ x: true, y: true }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) {
                      return null;
                    }
                    const item = payload[0];
                    return (
                      <div className="organic-tooltip organic-tooltip--kpi">
                        <div className="organic-tooltip__bar" />
                        <div className="organic-tooltip__content">
                          <div className="organic-tooltip__meta-line">
                            <span className="organic-tooltip__meta-label">
                              {t("engagementOverview.shared.totalUsersSegmentLabel")}
                            </span>
                            <strong className="organic-tooltip__meta-value">
                              {String(item.name ?? "")}
                            </strong>
                          </div>
                          <div className="organic-tooltip__meta-line">
                            <span className="organic-tooltip__meta-label">
                              {t("engagementOverview.shared.totalUsersCountLabel")}
                            </span>
                            <strong className="organic-tooltip__meta-value">
                              {formatTooltipCount(item.value, i18n.language)}
                            </strong>
                          </div>
                        </div>
                      </div>
                    );
                  }}
                />
                <Pie
                  data={slices}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius="58%"
                  outerRadius="86%"
                  startAngle={90}
                  endAngle={-270}
                  paddingAngle={0}
                  labelLine={false}
                >
                  {slices.map(slice => (
                    <Cell key={slice.name} fill={slice.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="engagement-overview-donut__center">
              <span className="engagement-overview-donut__center-value">
                {formatTooltipCount(total, i18n.language)}
              </span>
              <span className="engagement-overview-donut__center-label">
                {t("engagementOverview.shared.totalUsersCenterLabel")}
              </span>
            </div>
          </div>
          <div
            className={`engagement-overview-donut__legend ${
              legendItems.length > 3
                ? "engagement-overview-donut__legend--columns"
                : "engagement-overview-donut__legend--centered"
            }`}
            aria-hidden
          >
            {legendItems.map(item => (
              <div
                key={`${item.name}-${item.value}`}
                className="engagement-overview-donut__legend-item"
              >
                <span
                  className={`engagement-overview-donut__swatch engagement-overview-donut__swatch--${item.name
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                  style={item.color ? { backgroundColor: item.color } : undefined}
                />
                <span className="engagement-overview-donut__legend-text">{item.name}</span>
                <strong>{formatTooltipPercent(item.percent, i18n.language, 0)}</strong>
              </div>
            ))}
          </div>
        </>
      )}
    </article>
  );
};

export default TotalUsersOnWebsite;
